<template>
  <div class="setting-edit-product-v2">
    <el-form
      ref="formRef"
      :model="formData"
      label-position="top"
      label-width="120px"
    >
      <!-- Información básica del elemento tipo -->
      <div
        class="basic-info-section"
        :class="{
          'section-error':
            !validacionEstados.descripcionValida || !validacionEstados.elementoBaseValido,
        }"
      >
        <product-basic-info-form
          ref="basicInfoFormRef"
          v-model="modeloBasicInfo"
          :is-compact="true"
          :validacion-estados="validacionEstados"
        />
      </div>

      <!-- Unidad de venta -->
      <div class="unidades-medida-section">
        <div class="unidades-medida-title">Unidad de venta</div>
        <el-checkbox-group
          v-model="modelo.unidadesMedida"
          class="unidades-medida-grid"
        >
          <el-checkbox
            v-for="unidad in unidadesMedidaListData"
            :key="unidad.Codigo"
            :value="unidad.Codigo"
          >
            {{ unidad.Descripcion
            }}<span v-if="unidad.Abreviatura"> ({{ unidad.Abreviatura }})</span>
          </el-checkbox>
        </el-checkbox-group>
      </div>

      <!-- Paso 1 y 2 en componentes separados -->
      <el-collapse
        v-model="activeCollapse"
        class="collapse-section"
      >
        <!-- Paso 1: Procesos y categorías -->
        <el-collapse-item
          title="Paso 1 - Procesos y categorías"
          name="paso1"
        >
          <process-category-manager
            v-model="procesosConCategorias"
            :procesos-disponibles="procesosDisponiblesPaso1"
            :todos-los-procesos="procesosListData"
            :categorias-disponibles="categoriasListData"
            @categoria-changed="onCategoriaChanged"
            @multi-material-changed="onMultiMaterialChanged"
          />
        </el-collapse-item>

        <!-- Paso 2: Configuración de procesos -->
        <el-collapse-item
          title="Paso 2 - Secciones, recursos e items"
          name="paso2"
          :class="{ 'collapse-item-error': !validacionEstados.procesosConfiguracionValidos }"
        >
          <process-configuration-manager
            v-model="procesosConfiguracion"
            :procesos-con-categoria="modelo.procesosConCategorias"
            :procesos-disponibles="procesosDisponiblesPaso2"
            :todos-los-procesos="procesosListData"
            :secciones-disponibles="seccionesListData"
            :get-secciones-disponibles-para-proceso="getSeccionesDisponiblesParaProceso"
            :recursos-disponibles="recursosListData"
            :categorias-disponibles="categoriasListData"
            :procesos-con-categorias="modelo.procesosConCategorias"
            :materiales-disponibles="materialesDisponiblesPorCategoria"
            :procesos-con-error="procesosConErrorSeccion"
            @categoria-added="onCategoriaAdded"
          />
        </el-collapse-item>

        <!-- Paso 3: Valores por defecto de imposición (solo para productos con suaje) -->
        <el-collapse-item
          v-if="admiteSuaje"
          title="Paso 3 - Valores por defecto de imposición"
          name="paso3"
        >
          <p class="paso3-hint">
            Estos valores solo aplican a este producto. Si no se define un valor específico, se
            utiliza el valor global configurado en Configuraciones por defecto.
          </p>
          <div class="paso3-field">
            <el-checkbox v-model="usarValorGlobalGapDesarrollo">
              Usar valor global para la tolerancia de Gap en Desarrollo
            </el-checkbox>
            <div
              v-if="!usarValorGlobalGapDesarrollo"
              class="paso3-inputs"
            >
              <custom-input-number
                v-model="overridesImposicion.tolGapDesarrolloMin"
                label="Tolerancia gap desarrollo mínimo"
                unit="mm"
                :spanColumn="12"
                :settings="{ precision: 2, step: 1, controls: false }"
              />
              <custom-input-number
                v-model="overridesImposicion.tolGapDesarrolloMax"
                label="Tolerancia gap desarrollo máximo aaaa"
                unit="mm"
                :spanColumn="12"
                :settings="{ precision: 2, step: 1, controls: true }"
              />
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>

      <!-- Botones de acción -->
      <div class="action-buttons">
        <el-button @click="cancelar">Cancelar</el-button>
        <el-button
          type="primary"
          :loading="guardando"
          @click="guardar"
        >
          {{ props.configuracionCompleta.Codigo ? 'Guardar' : 'Crear' }}
        </el-button>
      </div>
    </el-form>

    <!-- Modal para editar materiales -->
    <el-dialog
      v-model="modalMaterialesVisible"
      title="Configurar items"
      width="70%"
      top="5vh"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="materials-modal"
    >
      <setting-materials-edit
        v-if="modalMaterialesVisible && seccionEditandose"
        :seccion="seccionEditandose"
        :categorias-materiales="categoriasDelProceso"
        @guardar="cerrarModalMateriales"
        @cancelar="cerrarModalMateriales"
      />
    </el-dialog>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  nextTick,
  watch,
} from 'vue';
import { ElMessage } from 'element-plus';
import { storeToRefs } from 'pinia';

// Componentes
import ProductBasicInfoForm from './product_basic_info_form.vue';
import ProcessCategoryManager from './process_category_manager.vue';
import ProcessConfigurationManager from './process_configuration_manager.vue';
import SettingMaterialsEdit from './setting_materials_edit.vue';

// Services
import ProcesoService from '@/services/proceso_service.js';
import { seccionesService } from '@/services/secciones_service.js';
import RecursoService from '@/services/recurso_service.js';
import MaterialCategoriaService from '@/services/material_categoria_service.js';
import MaterialService from '@/services/material_service.js';
import elementoTipoService from '@/services/elemento_tipo_service.js';
import { useUnidadMedidaStore } from '@/stores/unidad_medida/unidad_medida_store.js';
import { useConfiguracionesPorDefectoStore } from '@/views/configuraciones_por_defecto/stores/configuraciones_por_defecto_store.js';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
// Props
const props = defineProps({
  // codigo: {
  //   type: Number,
  //   required: false
  // },
  configuracionCompleta: {
    type: Object,
    required: false,
    default: null,
  },
  // modelValue: {
  //   type: Object,
  //   default: () => ({})
  // }
});

// Emits
const emit = defineEmits(['guardar', 'close', 'created']);

// Referencias
const formRef = ref();
const basicInfoFormRef = ref();

// Estado reactivo para datos de servicios
const procesosListData = ref([]);
const seccionesListData = ref([]);
const recursosListData = ref([]);
const categoriasListData = ref([]);
const materialesPorCategoriaCache = ref(new Map()); // Cache para materiales por categoría
const cargandoDatos = ref(false);

// Unidades de medida permitidas
const unidadMedidaStore = useUnidadMedidaStore();
const { unidadesMedida: unidadesMedidaListData } = storeToRefs(unidadMedidaStore);

// Estado reactivo principal - CENTRALIZADO
const modelo = ref({
  Codigo: null,
  Descripcion: '',
  ElementoBase: null,
  Activo: true,
  procesosConCategorias: [],
  procesosConfiguracion: [],
  unidadesMedida: [],
});

// Computed para las partes específicas del modelo
const modeloBasicInfo = ref({
  Descripcion: props.configuracionCompleta.Descripcion,
  ElementoBase: props.configuracionCompleta.ElementoBase,
  Activo: props.configuracionCompleta.Activo,
});

const procesosConCategorias = computed({
  get: () => modelo.value.procesosConCategorias || [],
  set: (value) => {
    modelo.value.procesosConCategorias = value;
  },
});

const procesosConfiguracion = computed({
  get: () => modelo.value.procesosConfiguracion || [],
  set: (value) => {
    modelo.value.procesosConfiguracion = value;
  },
});

// Compatibilidad con código legacy
const formData = computed(() => modelo.value);

// Computed properties para validación visual
const validacionEstados = computed(() => ({
  descripcionValida:
    modeloBasicInfo.value.Descripcion && modeloBasicInfo.value.Descripcion.trim().length >= 3,
  elementoBaseValido: modeloBasicInfo.value.ElementoBase.Codigo,
  procesosConfiguracionValidos: modelo.value.procesosConfiguracion.every(
    (proceso) => proceso.CodigoSeccion,
  ),
  tieneConfiguracionValida:
    modelo.value.procesosConfiguracion.length > 0 ||
    modelo.value.procesosConCategorias.some((p) =>
      p.categorias?.some((c) => c.CategoriaMaterial?.Codigo),
    ),
}));

// Computed para identificar procesos sin sección
const procesosConErrorSeccion = computed(() => {
  return new Set(
    modelo.value.procesosConfiguracion
      .filter((proceso) => !proceso.CodigoSeccion)
      .map((proceso) => proceso._uid),
  );
});

// const basicInfoData = ref({
//   Descripcion: formData.value.Descripcion || '',
//   elementoBase: null,
//   Activo: formData.value.Activo !== false,
//   Tintas: formData.value.Tintas || false
// })

// Estado de la UI
const activeCollapse = ref(['paso1', 'paso2']); // Ambos pasos abiertos por defecto
const modalMaterialesVisible = ref(false);
const seccionEditandose = ref(null);
const seccionIndex = ref({ grupoIndex: -1, seccionIndex: -1 });
const guardando = ref(false);

// Paso 3: override de valores por defecto de imposición para este producto puntual
const configuracionesPorDefectoStore = useConfiguracionesPorDefectoStore();
const usarValorGlobalGapDesarrollo = ref(true);
const overridesImposicion = ref({
  tolGapDesarrolloMin: null,
  tolGapDesarrolloMax: null,
});

const admiteSuaje = computed(() =>
  (modelo.value.procesosConfiguracion || []).some((proceso) => proceso.AdmiteSuaje),
);

async function cargarOverridesImposicion() {
  if (!props.configuracionCompleta?.Codigo) return;

  const overrides = await configuracionesPorDefectoStore.findOverridesByElementoTipoHTTP(
    props.configuracionCompleta.Codigo,
  );
  const min = overrides.find((o) => o.ClaveInterna === 'tol_gap_desarrollo_min');
  const max = overrides.find((o) => o.ClaveInterna === 'tol_gap_desarrollo_max');

  if (min || max) {
    usarValorGlobalGapDesarrollo.value = false;
    overridesImposicion.value = {
      tolGapDesarrolloMin: min ? Number(min.Valor) : null,
      tolGapDesarrolloMax: max ? Number(max.Valor) : null,
    };
  }
}

async function guardarOverridesImposicion() {
  if (!admiteSuaje.value || !props.configuracionCompleta?.Codigo) return;

  const overrides = usarValorGlobalGapDesarrollo.value
    ? [
        { ClaveInterna: 'tol_gap_desarrollo_min', Valor: null },
        { ClaveInterna: 'tol_gap_desarrollo_max', Valor: null },
      ]
    : [
        {
          ClaveInterna: 'tol_gap_desarrollo_min',
          Valor: overridesImposicion.value.tolGapDesarrolloMin,
        },
        {
          ClaveInterna: 'tol_gap_desarrollo_max',
          Valor: overridesImposicion.value.tolGapDesarrolloMax,
        },
      ];

  await configuracionesPorDefectoStore.updateOverridesByElementoTipoHTTP(
    props.configuracionCompleta.Codigo,
    overrides,
  );
}

// Estado para materiales disponibles
const materialesDisponiblesPorCategoria = ref(new Map());

// Computed para opciones disponibles
const procesosDisponiblesPaso1 = computed(() => {
  if (!procesosListData.value || !Array.isArray(procesosListData.value)) {
    console.warn('procesosListData no está disponible o no es un array:', procesosListData.value);
    return [];
  }

  const procesosUsados = modelo.value.procesosConCategorias
    .map((p) => p.CodigoProceso)
    .filter(Boolean);

  return procesosListData.value.filter((p) => p && p.Codigo && !procesosUsados.includes(p.Codigo));
});

const procesosDisponiblesPaso2 = computed(() => {
  if (!procesosListData.value || !Array.isArray(procesosListData.value)) {
    console.warn('procesosListData no está disponible o no es un array:', procesosListData.value);
    return [];
  }

  // En el Paso 2, mostrar TODOS los procesos disponibles
  // porque es válido agregar el mismo proceso para diferentes secciones
  return procesosListData.value
    .filter((p) => p && p.Codigo) // Solo filtrar procesos válidos
    .map((p) => ({
      Codigo: p.Codigo,
      Descripcion: p.Descripcion || `Proceso ${p.Codigo}`,
    }));
});

// Función para obtener las secciones disponibles para un proceso específico
const getSeccionesDisponiblesParaProceso = computed(() => {
  return (codigoProceso, seccionActualmenteSeleccionada = null) => {
    if (!seccionesListData.value || !Array.isArray(seccionesListData.value)) {
      return [];
    }

    // Obtener las secciones ya usadas por este proceso específico
    const seccionesUsadasPorProceso = modelo.value.procesosConfiguracion
      .filter((p) => p.CodigoProceso === codigoProceso && p.CodigoSeccion)
      .map((p) => p.CodigoSeccion);

    // Filtrar las secciones para excluir las ya usadas por este proceso
    // PERO incluir la sección actualmente seleccionada si existe
    const seccionesDisponibles = seccionesListData.value.filter((seccion) => {
      if (!seccion || !seccion.Codigo) return false;

      // Si es la sección actualmente seleccionada, siempre incluirla
      if (seccionActualmenteSeleccionada && seccion.Codigo === seccionActualmenteSeleccionada) {
        return true;
      }

      // Si no está usada por este proceso, incluirla
      return !seccionesUsadasPorProceso.includes(seccion.Codigo);
    });

    return seccionesDisponibles;
  };
});

const categoriasDelProceso = computed(() => {
  if (!seccionEditandose.value) return [];
  const grupoIndex = seccionIndex.value.grupoIndex;
  if (grupoIndex >= 0 && modelo.value.procesosConfiguracion[grupoIndex]) {
    const codigoProceso = modelo.value.procesosConfiguracion[grupoIndex].CodigoProceso;
    const procesoConCategorias = modelo.value.procesosConCategorias.find(
      (p) => p.CodigoProceso === codigoProceso,
    );
    return (
      procesoConCategorias?.categorias
        ?.map((cat) => cat.CategoriaMaterial?.Codigo)
        .filter(Boolean) || []
    );
  }
  return [];
});

// Métodos para cargar datos de servicios
const cargarProcesos = async () => {
  try {
    const result = await ProcesoService.findAll({ Activo: true, page: 0 });
    if (result && Array.isArray(result)) {
      procesosListData.value = result;
    } else {
      procesosListData.value = [];
    }
  } catch (error) {
    console.error('Error al cargar procesos:', error);
    procesosListData.value = [];
  }
};

const cargarSecciones = async () => {
  try {
    const result = await seccionesService.findAll();
    if (result && result.success && result.data && Array.isArray(result.data)) {
      seccionesListData.value = result.data;
    } else {
      seccionesListData.value = [];
    }
  } catch (error) {
    console.error('Error al cargar secciones:', error);
    seccionesListData.value = [];
  }
};

const cargarRecursos = async () => {
  try {
    const queryParams = 'page=0&Activo=true';
    const result = await RecursoService.findAll(queryParams);
    if (result && result.data && Array.isArray(result.data)) {
      recursosListData.value = result.data;
    } else {
      recursosListData.value = [];
    }
  } catch (error) {
    console.error('Error al cargar recursos:', error);
    recursosListData.value = [];
  }
};

const cargarCategorias = async () => {
  try {
    const result = await MaterialCategoriaService.findAll({
      page: 0,
    });
    if (result && result.data && Array.isArray(result.data)) {
      categoriasListData.value = result.data.filter((c) => c.Activo);
    } else {
      categoriasListData.value = [];
    }
  } catch (error) {
    console.error('Error al cargar categorías:', error);
    categoriasListData.value = [];
  }
};

const cargarUnidadesMedida = async () => {
  try {
    unidadMedidaStore.emptyList();
    await unidadMedidaStore.findAllUnidadMedidaHTTP({
      page: 0,
      Activo: true,
      EsUnidadVenta: true,
      sortBy: 'Descripcion',
      sort: 'Asc',
    });
  } catch (error) {
    console.error('Error al cargar unidades de medida:', error);
  }
};

// Función para cargar el objeto elemento base por código
// const cargarElementoBasePorCodigo = async (codigoElementoBase) => {
//   if (!codigoElementoBase) {
//     basicInfoData.value.elementoBase = null
//     return
//   }

//   try {
//     await elementoBaseStore.firstElementoBase()
//     const elementosBase = elementoBaseStore.elementosBase || []
//     const elementoBase = elementosBase.find(el => el.Codigo === codigoElementoBase)
//     basicInfoData.value.elementoBase = elementoBase || null
//   } catch (error) {
//     console.error('Error al cargar elemento base:', error)
//     basicInfoData.value.elementoBase = null
//   }
// }

// Función para cargar la configuración completa desde el backend
const cargarConfiguracionCompleta = async (codigoElementoTipo) => {
  if (!codigoElementoTipo) return;

  try {
    const response = await elementoTipoService.getConfiguracion(codigoElementoTipo);

    if (response && response.success && response.data) {
      const datos = response.data;

      // Función para generar IDs únicos
      const uid = () => Math.random().toString(36).slice(2, 9);

      // Actualizar información básica
      const { Descripcion, ElementoBase, Activo } = datos;
      modelo.value.Codigo = datos.Codigo || null;
      modelo.value.Descripcion = Descripcion;
      modelo.value.ElementoBase = ElementoBase;
      modelo.value.Activo = Activo;

      // modelo.value = {
      //   ...modelo.value,
      //   ...rest
      // }

      // Inicializar procesos con categorías (Paso 1) desde categoriasMaterial
      const procesosMap = new Map();

      if (datos.categoriasMaterial && Array.isArray(datos.categoriasMaterial)) {
        datos.categoriasMaterial.forEach((categoria) => {
          if (!procesosMap.has(categoria.Proceso.Codigo)) {
            procesosMap.set(categoria.Proceso.Codigo, {
              _uid: uid(),
              CodigoProceso: categoria.Proceso.Codigo,
              Proceso: categoria.Proceso,
              categorias: [],
            });
          }

          const proceso = procesosMap.get(categoria.Proceso.Codigo);
          proceso.categorias.push({
            _uid: uid(),
            CategoriaMaterial: categoria.CategoriaMaterial || null,
            MultiMaterial: categoria.MultiMaterial || false,
            SumaGramaje: categoria.SumaGramaje || false,
            DimensionesAutomaticas: categoria.DimensionesAutomaticas || false,
            CantidadManual: categoria.CantidadManual || false,
            PermiteReservar: categoria.PermiteReservar || false,
            ProcesoPrecedente: categoria.ProcesoPrecedente || false,
            Secciones: [],
          });
        });
      }

      procesosConCategorias.value = Array.from(procesosMap.values());
      modelo.value.procesosConCategorias = procesosConCategorias.value;

      // Inicializar configuración de procesos (Paso 2) desde procesos
      if (datos.procesos && Array.isArray(datos.procesos)) {
        procesosConfiguracion.value = datos.procesos.map((proceso) => {
          const nuevaConfiguracion = {
            _uid: uid(),
            CodigoProceso: proceso.Proceso.Codigo,
            Proceso: proceso.Proceso || null,
            CodigoSeccion: proceso.Seccion?.Codigo || null,
            Seccion: proceso.Seccion || null,
            PorDefecto: proceso.PorDefecto || false,
            AdmiteSuaje: proceso.AdmiteSuaje || false,
            CodigoRecurso: proceso.Recurso?.Codigo || null,
            Recurso: proceso.Recurso || null,
            codigosMateriales: proceso.codigosMateriales || [],
            materialesPorCategoria: {},
          };

          // Convertir materiales a la nueva estructura agrupada por categoría
          if (proceso.materiales && Array.isArray(proceso.materiales)) {
            proceso.materiales.forEach((material) => {
              // Obtener la categoría del material desde MaterialTipo.CodigoMaterialCategoria
              const codigoCategoria = material.MaterialTipo?.CodigoMaterialCategoria;

              if (codigoCategoria) {
                // Inicializar la categoría si no existe
                if (!nuevaConfiguracion.materialesPorCategoria[codigoCategoria]) {
                  nuevaConfiguracion.materialesPorCategoria[codigoCategoria] = [];
                }

                // Agregar el material a la categoría correspondiente
                nuevaConfiguracion.materialesPorCategoria[codigoCategoria].push({
                  materialId: material.Codigo,

                  _uid: uid(),
                });
              }
            });
          }

          // Fallback: si no hay materiales con categoría, usar codigosMateriales como lista simple
          if (
            Object.keys(nuevaConfiguracion.materialesPorCategoria).length === 0 &&
            proceso.codigosMateriales &&
            Array.isArray(proceso.codigosMateriales)
          ) {
            // Para materiales sin categoría específica, intentar agruparlos por categoría
            // utilizando la información de categoriasMaterial del paso 1
            const categoriasDelProceso =
              datos.categoriasMaterial?.filter(
                (cat) => cat.Proceso.Codigo === proceso.Proceso.Codigo,
              ) || [];

            if (categoriasDelProceso.length > 0) {
              // Si el proceso tiene categorías definidas, distribuir los materiales
              // Por ahora, asignar todos los materiales a la primera categoría disponible
              const primeraCategoria = categoriasDelProceso[0].CategoriaMaterial.Codigo;
              nuevaConfiguracion.materialesPorCategoria[primeraCategoria] =
                proceso.codigosMateriales.map((materialId) => ({
                  materialId,
                  _uid: uid(),
                }));
            }
          }

          return nuevaConfiguracion;
        });

        modelo.value.procesosConfiguracion = procesosConfiguracion.value;
      }

      modelo.value.procesosConfiguracion = procesosConfiguracion.value;

      // Precargar materiales para las categorías existentes
      await precargarMaterialesPorCategorias();
    } else {
      //else
    }
  } catch (error) {
    console.error('Error al cargar configuración:', error);
    ElMessage.warning(
      'No se pudo cargar la configuración del producto o no existe configuración previa',
    );
  }
};

// Función para cargar materiales por categoría
const cargarMaterialesPorCategoria = async (codigoCategoria) => {
  if (!codigoCategoria) {
    console.warn('⚠️  Intento de cargar items sin código de categoría');
    return [];
  }

  // Verificar si ya está en cache
  if (materialesPorCategoriaCache.value.has(codigoCategoria)) {
    const materialesCache = materialesPorCategoriaCache.value.get(codigoCategoria);
    return materialesCache;
  }

  try {
    const result = await MaterialService.findActiveMaterials(
      {
        categoria: codigoCategoria,
      },
      1,
      100,
    ); // Obtener hasta 100 materiales por categoría

    let materiales = [];

    if (result && result.data && Array.isArray(result.data)) {
      materiales = result.data.map((material) => ({
        label: material.DescripcionInterna,
        value: material.Codigo,
      }));
    } else {
      console.log('📭 Sin items encontrados para categoría:', codigoCategoria);
    }

    // Guardar en cache
    materialesPorCategoriaCache.value.set(codigoCategoria, materiales);

    return materiales;
  } catch (error) {
    console.error(`❌ Error al cargar items de la categoría ${codigoCategoria}:`, error);
    materialesPorCategoriaCache.value.set(codigoCategoria, []);
    return [];
  }
};

// Función para precargar materiales de todas las categorías activas
const precargarMaterialesPorCategorias = async () => {
  const categoriasActivas = new Set();

  // Obtener todas las categorías usadas en los procesos
  modelo.value.procesosConCategorias.forEach((proceso) => {
    if (proceso.categorias) {
      proceso.categorias.forEach((categoria) => {
        if (categoria.CategoriaMaterial?.Codigo) {
          categoriasActivas.add(categoria.CategoriaMaterial.Codigo);
        }
      });
    }
  });

  // Si no hay categorías activas, limpiar el mapa de materiales
  if (categoriasActivas.size === 0) {
    materialesDisponiblesPorCategoria.value.clear();
    materialesDisponiblesPorCategoria.value = new Map();
    return;
  }

  // Cargar materiales para cada categoría
  const promesasCarga = Array.from(categoriasActivas).map(async (codigoCategoria) => {
    try {
      const materiales = await cargarMaterialesPorCategoria(codigoCategoria);
      materialesDisponiblesPorCategoria.value.set(codigoCategoria, materiales);
    } catch (error) {
      console.error(`❌ Error al precargar items para categoría ${codigoCategoria}:`, error);
      materialesDisponiblesPorCategoria.value.set(codigoCategoria, []);
    }
  });

  await Promise.all(promesasCarga);

  // Forzar reactividad
  materialesDisponiblesPorCategoria.value = new Map(materialesDisponiblesPorCategoria.value);
};

// Event handlers para los componentes hijos - Optimizado
const onCategoriaChanged = async (categoria) => {
  // Si hay una nueva categoría, cargar sus materiales inmediatamente
  if (categoria.CategoriaMaterial?.Codigo) {
    try {
      const materiales = await cargarMaterialesPorCategoria(categoria.CategoriaMaterial.Codigo);
      materialesDisponiblesPorCategoria.value.set(categoria.CategoriaMaterial.Codigo, materiales);

      // Forzar reactividad
      materialesDisponiblesPorCategoria.value = new Map(materialesDisponiblesPorCategoria.value);
    } catch (error) {
      console.error('❌ Error al cargar items para la nueva categoría:', error);
    }
  }

  // Usar nextTick para evitar múltiples re-renders
  await nextTick();

  // Precargar materiales para todas las categorías activas (incluye la nueva)
  await precargarMaterialesPorCategorias();

  // Forzar reactividad para actualizar las opciones de categorías disponibles
  nextTick(() => {
    procesosConCategorias.value = [...procesosConCategorias.value];
  });
};

const onMultiMaterialChanged = async ({ proceso, categoria }) => {
  // Obtener el código de categoría de material
  const codigoCategoriaMaterial = categoria.CategoriaMaterial?.Codigo;

  // Si no hay código de categoría válido, no podemos proceder
  if (!codigoCategoriaMaterial) {
    return;
  }

  // Si se desmarca MultiMaterial (cambia a false)
  if (!categoria.MultiMaterial) {
    let materialesEliminadosTotal = 0;
    let seccionesAfectadas = 0;

    // Buscar TODAS las configuraciones de procesos que coincidan con el código de proceso
    // (puede haber múltiples configuraciones para el mismo proceso con diferentes secciones)
    const configuracionesProceso = modelo.value.procesosConfiguracion.filter(
      (p) => p.CodigoProceso === proceso.CodigoProceso,
    );

    configuracionesProceso.forEach((configuracionProceso) => {
      if (configuracionProceso?.materialesPorCategoria?.[codigoCategoriaMaterial]) {
        const materialesCategoria =
          configuracionProceso.materialesPorCategoria[codigoCategoriaMaterial];

        // Si hay más de un material, mantener solo el primero
        if (materialesCategoria.length > 1) {
          const materialesEliminados = materialesCategoria.length - 1;
          materialesEliminadosTotal += materialesEliminados;
          seccionesAfectadas++;

          // Mantener solo el primer material
          configuracionProceso.materialesPorCategoria[codigoCategoriaMaterial] = [
            materialesCategoria[0],
          ];
        }
      }
    });

    // Forzar reactividad actualizando la referencia del array
    modelo.value.procesosConfiguracion = [...modelo.value.procesosConfiguracion];
    procesosConfiguracion.value = modelo.value.procesosConfiguracion;

    // Mostrar mensaje informativo si se eliminaron materiales
    if (materialesEliminadosTotal > 0) {
      const categoriaDescripcion =
        categoria.CategoriaMaterial?.Descripcion || `categoría ${codigoCategoriaMaterial}`;
      const procesoDescripcion = proceso.Proceso?.Descripcion || `proceso ${proceso.CodigoProceso}`;

      const mensaje = `Se eliminaron ${materialesEliminadosTotal} items adicionales de la ${categoriaDescripcion} en ${seccionesAfectadas} sección(es) del ${procesoDescripcion}. Solo se mantuvieron los primeros items de cada sección.`;

      ElMessage.info(mensaje);
    }
  }
};

// Métodos principales
const guardar = async () => {
  // TODO: limpiar esta funcion
  if (!formRef.value) return;

  const { ElementoBase, ...rest } = modeloBasicInfo.value;

  modelo.value = {
    ...modelo.value,
    ElementoBase,
    CodigoElementoBase: ElementoBase?.Codigo ?? null,
    ...rest,
  };
  // Forzar actualización de validación
  const validacionActual = validacionEstados.value;

  // Validaciones básicas requeridas
  if (!validacionActual.descripcionValida) {
    ElMessage.warning('La descripción debe tener al menos 3 caracteres');
    return;
  }

  if (!validacionActual.elementoBaseValido) {
    ElMessage.warning('Debe seleccionar un elemento base');
    return;
  }

  // Validación del Paso 2: Todos los procesos deben tener sección seleccionada
  const procesosConfiguracionSinSeccion = modelo.value.procesosConfiguracion.filter(
    (proceso) => !proceso.CodigoSeccion,
  );

  if (procesosConfiguracionSinSeccion.length > 0) {
    const procesosDescripcion = procesosConfiguracionSinSeccion
      .map((p) => p.Proceso?.Descripcion || `Proceso ${p.CodigoProceso}`)
      .join(', ');

    ElMessage.warning(
      `Los siguientes procesos del Paso 2 no tienen sección seleccionada: ${procesosDescripcion}. Por favor, selecciona una sección para cada proceso o elimínalos.`,
    );
    return;
  }

  guardando.value = true;

  try {
    // Generar estructura de procesos para el backend
    const procesos = [];
    const categoriasMaterial = [];

    // Procesar configuración de procesos (Paso 2) - solo elementos válidos
    modelo.value.procesosConfiguracion.forEach((proceso, index) => {
      const codigoProceso = proceso.CodigoProceso;
      // Solo procesar si tiene sección asignada
      if (!proceso.CodigoSeccion) {
        console.warn(`Proceso ${codigoProceso} omitido - no tiene sección asignada`);
        return; // Saltar este proceso
      }

      // Obtener códigos de materiales del proceso como array simple de números
      let codigosMateriales = [];

      // Recopilar materiales de la nueva estructura agrupada por categoría
      if (proceso.materialesPorCategoria) {
        Object.keys(proceso.materialesPorCategoria).forEach((codigoCategoria) => {
          const materialesCategoria = proceso.materialesPorCategoria[codigoCategoria];
          materialesCategoria.forEach((material) => {
            // Solo agregar materiales que tengan un ID válido
            if (material.materialId != null && material.materialId !== '') {
              codigosMateriales.push(Number(material.materialId));
            }
          });
        });
      }

      // Fallback: usar estructura antigua si no hay nueva estructura
      if (codigosMateriales.length === 0 && proceso.codigosMateriales) {
        codigosMateriales = proceso.codigosMateriales
          .filter((m) => m != null && m !== '') // Filtrar elementos vacíos o nulos
          .map((m) => {
            // Si es un objeto con materialId, extraer solo el ID
            if (typeof m === 'object' && m.materialId != null && m.materialId !== '') {
              return Number(m.materialId);
            }
            // Si ya es un número o string, convertir a número
            return Number(m);
          })
          .filter((id) => !isNaN(id) && id > 0); // Filtrar valores inválidos y <= 0
      }

      procesos.push({
        CodigoProceso: Number(codigoProceso),
        CodigoSeccion: proceso.CodigoSeccion ? Number(proceso.CodigoSeccion) : null,
        PorDefecto: Boolean(proceso.PorDefecto),
        AdmiteSuaje: Boolean(proceso.AdmiteSuaje),
        CodigoRecurso: proceso.CodigoRecurso ? Number(proceso.CodigoRecurso) : null,
        codigosMateriales,
        Orden: index + 1,
      });
    });
    // Procesar categorías de material (Paso 1) - solo elementos válidos
    modelo.value.procesosConCategorias.forEach((procesoConCats) => {
      if (procesoConCats.CodigoProceso && procesoConCats.categorias) {
        procesoConCats.categorias.forEach((categoria) => {
          // Solo agregar si tiene categoría de material asignada
          if (categoria.CategoriaMaterial?.Codigo) {
            categoriasMaterial.push({
              CodigoProceso: Number(procesoConCats.CodigoProceso),
              CodigoCategoriaMaterial: Number(categoria.CategoriaMaterial.Codigo),
              MultiMaterial: Boolean(categoria.MultiMaterial),
              SumaGramaje: Boolean(categoria.SumaGramaje),
              DimensionesAutomaticas: Boolean(categoria.DimensionesAutomaticas),
              CantidadManual: Boolean(categoria.CantidadManual),
              PermiteReservar: Boolean(categoria.PermiteReservar),
              ProcesoPrecedente: Boolean(categoria.ProcesoPrecedente),
            });
          } else {
            console.warn(
              `Categoría omitida - proceso ${procesoConCats.CodigoProceso} sin categoría de item asignada`,
            );
          }
        });
      }
    });

    // Validar que hay datos válidos para enviar
    if (
      procesos.length === 0 &&
      categoriasMaterial.length === 0 &&
      modelo.value.unidadesMedida.length === 0
    ) {
      ElMessage.warning(
        'No hay configuraciones válidas para guardar. Asegúrate de completar al menos un proceso con sección asignada, categorías con item asignado, o una unidad de medida permitida.',
      );
      return;
    }

    // Estructura completa para el backend
    const datosCompletos = {
      CodigoElementoBase: modelo.value.ElementoBase?.Codigo
        ? Number(modelo.value.ElementoBase.Codigo)
        : null,
      procesos,
      categoriasMaterial,
      unidadesMedida: (modelo.value.unidadesMedida || []).map(Number),
      ...rest,
      Activo: !!rest.Activo,
    };

    // Determinar si es creación o actualización
    if (props.configuracionCompleta.Codigo) {
      // Actualizar producto existente
      elementoTipoService
        .updateConfiguracion(props.configuracionCompleta.Codigo, datosCompletos)
        .then(async () => {
          await guardarOverridesImposicion();
          ElMessage.success('Producto actualizado correctamente');
          emit('guardar', {
            Codigo: props.configuracionCompleta.Codigo,
            Descripcion: modelo.value.Descripcion,
            CodigoElementoBase: modelo.value.ElementoBase?.Codigo || null,
            elementoBase: modelo.value.ElementoBase,
            Activo: modelo.value.Activo,
            ElementoBase: modelo.value.ElementoBase,
          });
        })
        .catch((error) => {
          console.error('Error al actualizar producto:', error);
          ElMessage.error('Error al actualizar el producto');
        });
    } else {
      // Crear nuevo producto
      const datosBasicos = {
        ...modeloBasicInfo.value,
        CodigoElementoBase: modelo.value.ElementoBase?.Codigo,
        Activo: !!modelo.value.Activo,
      };

      elementoTipoService
        .create(datosBasicos)
        .then(async (productoCreado) => {
          if (productoCreado && productoCreado.Codigo) {
            const codigoNuevo = productoCreado.Codigo;

            // Si hay configuración de procesos, categorías o unidades de medida, actualizar la configuración
            if (
              procesos.length > 0 ||
              categoriasMaterial.length > 0 ||
              datosCompletos.unidadesMedida.length > 0
            ) {
              try {
                await elementoTipoService.updateConfiguracion(codigoNuevo, datosCompletos);
              } catch (configError) {
                console.error('Error al configurar el producto creado:', configError);
                ElMessage.warning(
                  'Producto creado pero hubo un error al configurar procesos y categorías',
                );
              }
            }

            ElMessage.success('Producto creado correctamente');

            const datosRespuesta = {
              Codigo: codigoNuevo,
              Descripcion: modelo.value.Descripcion,
              CodigoElementoBase: modelo.value.ElementoBase?.Codigo || null,
              elementoBase: modelo.value.ElementoBase,
              Activo: modelo.value.Activo,
              ElementoBase: modelo.value.ElementoBase,
            };

            // Emitir evento de creación para actualizar listas
            emit('created', datosRespuesta);
            // También emitir guardar para compatibilidad
            emit('guardar', datosRespuesta);
          } else {
            throw new Error('No se recibió el código del producto creado');
          }
        })
        .catch((error) => {
          console.error('Error al crear producto:', error);
          ElMessage.error('Error al crear el producto');
        });
    }
  } catch (error) {
    console.error('Error al guardar:', error);
    ElMessage.error('Error al guardar los datos');
  } finally {
    guardando.value = false;
  }
};

const cancelar = () => {
  emit('close');
};

const cerrarModalMateriales = () => {
  modalMaterialesVisible.value = false;
  seccionEditandose.value = null;
  seccionIndex.value = { grupoIndex: -1, seccionIndex: -1 };
};

const inicializarDatos = async () => {
  // Si se pasó una configuración completa como prop, usarla directamente
  if (props.configuracionCompleta) {
    await aplicarConfiguracionCompleta(props.configuracionCompleta);
  }
};

// Función para aplicar configuración ya cargada
const aplicarConfiguracionCompleta = async (configuracion) => {
  if (!configuracion) return;

  console.log('🔄 Aplicando configuración completa:', configuracion);

  // Aplicar la configuración al modelo de forma reactiva
  modelo.value = {
    ...modelo.value,
    Codigo: configuracion.Codigo,
    Descripcion: configuracion.Descripcion || '',
    ElementoBase: configuracion.ElementoBase,
    Activo: configuracion.Activo !== undefined ? configuracion.Activo : true,
    procesosConCategorias: configuracion.procesosConCategorias || [],
    procesosConfiguracion: configuracion.procesosConfiguracion || [],
    unidadesMedida: configuracion.unidadesMedida || [],
  };

  // Usar nextTick para asegurar que los cambios se propaguen correctamente
  await nextTick();

  // Precargar materiales para las categorías existentes si hay procesos configurados
  if (configuracion.procesosConCategorias && configuracion.procesosConCategorias.length > 0) {
    await precargarMaterialesPorCategorias();
  }
};

// Watcher para monitorear cambios en configuracionCompleta
watch(
  () => props.configuracionCompleta,
  async (nuevaConfiguracion) => {
    if (nuevaConfiguracion) {
      await aplicarConfiguracionCompleta(nuevaConfiguracion);
    }
  },
  {
    deep: true,
    immediate: false, // No ejecutar inmediatamente porque ya se maneja en onMounted
  },
);

onMounted(async () => {
  try {
    cargandoDatos.value = true;

    // Cargar datos necesarios en paralelo
    await Promise.all([
      cargarProcesos(),
      cargarSecciones(),
      cargarRecursos(),
      cargarCategorias(),
      cargarUnidadesMedida(),
    ]);

    // Inicializar datos desde props (esto ahora puede cargar configuración del backend)
    await inicializarDatos();
    await cargarOverridesImposicion();
  } catch (error) {
    console.error('Error al cargar datos:', error);
    ElMessage.error('Error al cargar los datos necesarios');
  } finally {
    cargandoDatos.value = false;
  }
});

const onCategoriaAdded = () => {
  precargarMaterialesPorCategorias();
};

// Exponer métodos para uso externo
defineExpose({
  cargarConfiguracionCompleta,
  aplicarConfiguracionCompleta,
  inicializarDatos,
  guardar,
});
</script>

<style scoped>
.setting-edit-product-v2 {
  padding: 20px;
  overflow-y: auto;
  max-height: 100%;
}

.basic-info-section {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid #e9ecef;
}

.unidades-medida-section {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid #e9ecef;
}

.unidades-medida-title {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 12px;
}

.unidades-medida-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 4px 12px;
}

.collapse-section {
  margin-bottom: 24px;
}

.collapse-section :deep(.el-collapse-item__header) {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
  padding: 16px;
}

.collapse-section :deep(.el-collapse-item__content) {
  padding: 16px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-top: none;
  border-radius: 0 0 8px 8px;
}

.collapse-section :deep(.el-collapse-item__wrap) {
  border: none;
}

.collapse-section :deep(.el-collapse) {
  border: none;
}

.collapse-section :deep(.el-collapse-item) {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  margin-bottom: 16px;
}

.paso3-hint {
  color: var(--el-text-color-secondary);
  font-size: 0.85rem;
  margin: 0 0 12px;
}

.paso3-inputs {
  display: flex;
  gap: 16px;
  margin-top: 12px;
}

.action-buttons {
  margin-top: 32px;
  text-align: right;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

.action-buttons .el-button {
  margin-left: 8px;
}

.materials-modal :deep(.el-dialog__body) {
  padding: 20px;
}

/* Estilos para validación visual */
.validation-error {
  border: 2px solid #f56565 !important;
  border-radius: 4px;
}

.validation-error :deep(.el-input__wrapper) {
  border: 2px solid #f56565 !important;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(245, 101, 101, 0.2);
}

.validation-error :deep(.el-select__wrapper) {
  border: 2px solid #f56565 !important;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(245, 101, 101, 0.2);
}

.validation-warning {
  border: 2px solid #ed8936 !important;
  border-radius: 4px;
}

.validation-warning :deep(.el-input__wrapper) {
  border: 2px solid #ed8936 !important;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(237, 137, 54, 0.2);
}

.validation-warning :deep(.el-select__wrapper) {
  border: 2px solid #ed8936 !important;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(237, 137, 54, 0.2);
}

/* Efecto de pulse para campos con error */
@keyframes validation-pulse {
  0% {
    box-shadow: 0 0 0 2px rgba(245, 101, 101, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(245, 101, 101, 0.1);
  }
  100% {
    box-shadow: 0 0 0 2px rgba(245, 101, 101, 0.4);
  }
}

.validation-error:focus-within {
  animation: validation-pulse 1.5s infinite;
}

/* Estilos para secciones con error */
.section-error {
  border-left: 4px solid #f56565;
  background-color: rgba(245, 101, 101, 0.05);
}

.collapse-item-error :deep(.el-collapse-item__header) {
  background: rgba(245, 101, 101, 0.1) !important;
  border-left: 4px solid #f56565;
}
</style>
